import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/** Above this, loading every grid page is too heavy: Enter sends the term to the search page instead. */
const MAX_LOCAL_PRODUCTS = 1000;
const PRODUCTS_JSON_PAGE_SIZE = 250;

/** @param {string} text */
const normalize = (text) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** @param {string} text */
const compact = (text) => normalize(text).replace(/[^a-z0-9]/g, '');

/**
 * @typedef {object} PoolItem
 * @property {string} productId
 * @property {HTMLElement} element - The collection's own grid item (`li.product-grid__item`)
 */

/**
 * Text filter for the collection grid. Typing shrinks the grid to the matching product cards.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} summary
 * @property {HTMLButtonElement} clearButton
 * @extends {Component<Refs>}
 */
class CollectionSearchComponent extends Component {
  requiredRefs = ['input', 'summary', 'clearButton'];

  #controller = new AbortController();

  /** @type {Promise<PoolItem[]> | null} */
  #pool = null;

  /** @type {Promise<Map<string, string>> | null} Product id → normalized name, references, type and tags */
  #index = null;

  /** @type {Node[] | null} The grid items of the current page, restored when the filter is cleared */
  #originalItems = null;

  #filterRun = 0;

  connectedCallback() {
    super.connectedCallback();
    // Facets re-render the section with new results: start over with the new grid
    document.addEventListener(ThemeEvents.FilterUpdate, this.#reset, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  get #filtersLocally() {
    return Number(this.dataset.productsCount) <= MAX_LOCAL_PRODUCTS;
  }

  get #grid() {
    const wrapper = this.closest('.collection-wrapper');
    return wrapper?.querySelector('ul.product-grid') ?? null;
  }

  /** Starts loading as soon as the customer focuses the box, so filtering is instant when typing. */
  prepare = () => {
    if (!this.#filtersLocally) return;
    this.#loadPool().catch(() => {});
    this.#loadIndex().catch(() => {});
  };

  filter = debounce(() => {
    this.#applyFilter(this.refs.input.value.trim());
  }, 180);

  clear = () => {
    this.refs.input.value = '';
    this.refs.input.focus();
    this.#applyFilter('');
  };

  /** @param {KeyboardEvent} event */
  onKeyDown = (event) => {
    if (event.key === 'Escape' && this.refs.input.value) {
      event.preventDefault();
      this.clear();
    }
  };

  /**
   * Enter: small collections already show the matches; big ones go to the search page.
   * @param {SubmitEvent} event
   */
  onSubmit = (event) => {
    if (this.#filtersLocally || !this.refs.input.value.trim()) {
      event.preventDefault();
      this.refs.input.blur();
    }
  };

  #reset = () => {
    this.#pool = null;
    this.#originalItems = null;
    this.refs.input.value = '';
    this.refs.clearButton.hidden = true;
    this.refs.summary.textContent = '';
    this.removeAttribute('data-filtering');
  };

  /** @param {string} term */
  async #applyFilter(term) {
    const run = ++this.#filterRun;
    const grid = this.#grid;
    this.refs.clearButton.hidden = term.length === 0;

    if (!grid) return;

    if (term.length < 2) {
      this.#restore(grid);
      return;
    }

    if (!this.#filtersLocally) {
      this.refs.summary.textContent = `Presiona Enter para buscar «${term}» en toda la tienda`;
      return;
    }

    this.#originalItems ??= Array.from(grid.children);
    this.refs.summary.textContent = 'Filtrando…';

    try {
      const [pool, index] = await Promise.all([this.#loadPool(), this.#loadIndex().catch(() => new Map())]);
      if (run !== this.#filterRun) return;

      const tokens = normalize(term).split(/\s+/).filter(Boolean);
      const compactTerm = compact(term);

      const matches = pool.filter(({ productId, element }) => {
        const text = index.get(productId) ?? normalize(element.textContent ?? '');
        if (tokens.every((token) => text.includes(token))) return true;
        return compactTerm.length >= 3 && compact(text).includes(compactTerm);
      });

      grid.replaceChildren(...matches.map((match) => match.element));
      this.setAttribute('data-filtering', '');
      this.refs.summary.textContent =
        matches.length === 0
          ? `No hay productos con «${term}» en esta colección`
          : `${matches.length} ${matches.length === 1 ? 'producto coincide' : 'productos coinciden'} con «${term}»`;
    } catch (error) {
      if (run !== this.#filterRun) return;
      this.refs.summary.textContent = 'No pudimos filtrar los productos. Intenta de nuevo.';
      console.error(error);
    }
  }

  /** @param {Element} grid */
  #restore(grid) {
    if (this.#originalItems) {
      grid.replaceChildren(...this.#originalItems);
    }
    this.removeAttribute('data-filtering');
    this.refs.summary.textContent = '';
  }

  /**
   * Loads every page of the grid with the current filters and sorting, as server-rendered cards.
   * @returns {Promise<PoolItem[]>}
   */
  #loadPool() {
    this.#pool ??= (async () => {
      const firstPage = await this.#fetchGridPage(1);
      const lastPage = Number(firstPage.grid?.getAttribute('data-last-page') ?? 1);
      const otherPages = await Promise.all(
        Array.from({ length: Math.max(0, lastPage - 1) }, (_, index) => this.#fetchGridPage(index + 2))
      );

      const seen = new Set();
      return [firstPage, ...otherPages]
        .flatMap((page) => page.items)
        .filter((item) => {
          if (seen.has(item.productId)) return false;
          seen.add(item.productId);
          return true;
        });
    })().catch((error) => {
      this.#pool = null;
      throw error;
    });

    return this.#pool;
  }

  /** @param {number} page */
  async #fetchGridPage(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', this.dataset.sectionId ?? '');

    const html = await fetch(url, { signal: this.#controller.signal }).then((response) => response.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.querySelector('ul.product-grid');

    const items = Array.from(grid?.querySelectorAll(':scope > li.product-grid__item') ?? []).map((item) => ({
      productId: item.getAttribute('data-product-id') ?? '',
      element: /** @type {HTMLElement} */ (document.importNode(item, true)),
    }));

    return { grid, items };
  }

  /**
   * Searchable text per product (references live in the variant SKUs, which the cards don't show).
   * @returns {Promise<Map<string, string>>}
   */
  #loadIndex() {
    this.#index ??= (async () => {
      const root = window.Shopify?.routes?.root ?? '/';
      const pages = Math.max(1, Math.ceil(Number(this.dataset.productsCount) / PRODUCTS_JSON_PAGE_SIZE));
      const responses = await Promise.all(
        Array.from({ length: pages }, (_, page) =>
          fetch(
            `${root}collections/${this.dataset.collectionHandle}/products.json?limit=${PRODUCTS_JSON_PAGE_SIZE}&page=${page + 1}`,
            { signal: this.#controller.signal }
          ).then((response) => response.json())
        )
      );

      /** @type {Map<string, string>} */
      const index = new Map();
      for (const response of responses) {
        for (const product of response.products ?? []) {
          const skus = (product.variants ?? []).map((/** @type {any} */ variant) => variant.sku ?? '').join(' ');
          const text = [product.title, skus, product.product_type, (product.tags ?? []).join(' ')].join(' ');
          index.set(String(product.id), normalize(text));
        }
      }
      return index;
    })().catch((error) => {
      this.#index = null;
      throw error;
    });

    return this.#index;
  }
}

if (!customElements.get('collection-search-component')) {
  customElements.define('collection-search-component', CollectionSearchComponent);
}
